import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { parseISO, getDaysInMonth, isToday } from 'date-fns'
import { Target, TrendingUp, AlertCircle, CheckCircle2, XCircle, DollarSign, Lightbulb, ShieldCheck, TrendingDown, Wallet, Calendar, Activity, Minus, Info, AlertTriangle, Clock } from 'lucide-react'
import { apiService } from '@/services/api.service'
import { Tooltip } from '@/components/ui/Tooltip'
import { CalculationsService } from '@/services/calculations'
import { useAppStore } from '@/lib/store'
import { formatCurrency, formatPercent, cn } from '@/lib/utils'
import type { SpreadsheetTotals } from '@/lib/types'

// Multiplicadores para meta diária baseado no perfil de risco
// Conservador: banca/100 (1%), Moderado: banca/50 (2%), Agressivo: banca/20 (5%)
const DAILY_GOAL_MULTIPLIERS = {
  conservative: { divisor: 100, percent: 1 },
  moderate: { divisor: 50, percent: 2 },
  aggressive: { divisor: 20, percent: 5 },
} as const

// Textos explicativos para tooltips da seção Meta Diária
const GOAL_TOOLTIPS = {
  suaMeta: 'Sua meta diária de lucro baseada na % selecionada sobre o capital atual.',
  lucroHoje: 'Resultado das suas operações finalizadas hoje (ganhos - perdas). Considera apenas trades com saldo real.',
  progresso: 'Quanto você já alcançou da sua meta diária. Ao atingir 100%, considere parar de operar para proteger seus ganhos.',
}

// Componente de ícone de info com tooltip
function InfoTooltip({ content }: { content: string }) {
  return (
    <Tooltip content={content} side="top" delayDuration={100}>
      <button type="button" className="ml-1 inline-flex items-center text-muted-foreground/50 hover:text-muted-foreground transition-colors">
        <Info className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  )
}

interface SpreadsheetRow {
  day: number
  date: string
  capitalAtual: number
  aportes: number
  retiradas: number
  valorOrdem: string
  metaDoDia: number
  valorPorEntrada: number
  qtEntradas: number
  metaBatida: boolean
  lucroDiaReais: number
  lucroDiaPercent: number
}
import { SpreadsheetControls } from './SpreadsheetControls'

export function SigmaSpreadsheet() {
  // Estado local: mês/ano selecionado
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth())
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear())

  // Estado global: perfil de risco, goalPercent e settings (persistidos)
  const { riskProfile, setRiskProfile, goalPercent, setGoalPercent } = useAppStore()

  // Fetch trades from API
  const { data: trades = [], isLoading: tradesLoading } = useQuery({
    queryKey: ['trades'],
    queryFn: () => apiService.fetchTrades(),
    refetchInterval: 5 * 60 * 1000,
  })

  // Fetch balance from API
  const { data: currentBalance = 0, isLoading: balanceLoading } = useQuery({
    queryKey: ['balance'],
    queryFn: () => apiService.fetchBalance(),
    refetchInterval: 5 * 60 * 1000,
  })

  // Calcular dados da planilha
  const { rows, totals } = useMemo(() => {
    const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth))
    const multiplier = DAILY_GOAL_MULTIPLIERS[riskProfile]
    const valorOrdem = `${multiplier.percent.toFixed(2).replace('.', ',')}%`

    // Filtrar trades do mês/ano selecionado
    const filteredTrades = trades.filter((trade) => {
      if (trade.demo || trade.status === 'pending' || trade.status === 'open') return false
      const tradeDate = parseISO(trade.created_at)
      return tradeDate.getMonth() === selectedMonth && tradeDate.getFullYear() === selectedYear
    })

    // Agrupar por dia usando CalculationsService
    const dailyStats = CalculationsService.groupByDay(filteredTrades)

    // Calcular lucro total do mês para determinar capital inicial
    const monthProfit = dailyStats.reduce((sum, d) => sum + d.profit, 0)

    // Calcular capital inicial do mês (saldo atual - lucro do mês)
    let runningCapital = currentBalance - monthProfit

    const rows: SpreadsheetRow[] = []
    let totalLucroReais = 0
    let totalEntradas = 0

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const dayStats = dailyStats.find((d) => d.date === dateStr)

      const capitalAtual = runningCapital
      const lucroDia = dayStats?.profit || 0
      const qtEntradas = dayStats?.trades || 0

      // Valor de entrada baseado no perfil de risco (% do capital)
      const valorPorEntrada = capitalAtual * (multiplier.percent / 100)

      const lucroDiaPercent = capitalAtual > 0 ? (lucroDia / capitalAtual) * 100 : 0

      // Meta do dia: % do capital do dia (dinâmica)
      const metaDoDia = capitalAtual * (goalPercent / 100)
      const metaBatida = lucroDia >= metaDoDia && qtEntradas > 0

      rows.push({
        day,
        date: dateStr,
        capitalAtual,
        aportes: 0, // API não disponibiliza por dia
        retiradas: 0, // API não disponibiliza por dia
        valorOrdem,
        metaDoDia,
        valorPorEntrada,
        qtEntradas,
        metaBatida,
        lucroDiaReais: lucroDia,
        lucroDiaPercent,
      })

      runningCapital += lucroDia
      totalLucroReais += lucroDia
      totalEntradas += qtEntradas
    }

    // Calcular capital inicial do mês para % total
    const capitalInicialMes = currentBalance - monthProfit
    const totalLucroPercent = capitalInicialMes > 0 ? (totalLucroReais / capitalInicialMes) * 100 : 0

    const totals: SpreadsheetTotals = {
      totalLucroReais,
      totalLucroPercent,
      totalEntradas,
    }

    return { rows, totals }
  }, [trades, selectedMonth, selectedYear, riskProfile, currentBalance, goalPercent])

  // Calcular meta diária e progresso de hoje
  const dailyGoalData = useMemo(() => {
    const dailyGoal = currentBalance * (goalPercent / 100)

    // Calcular lucro de hoje
    const todayTrades = trades.filter((trade) => {
      if (trade.demo || trade.status === 'pending' || trade.status === 'open') return false
      return isToday(parseISO(trade.created_at))
    })
    const todayProfit = todayTrades.reduce((sum, t) => sum + (t.profit || 0), 0)
    const todayTotalTrades = todayTrades.length
    const todayWins = todayTrades.filter((t) => (t.profit || 0) > 0).length

    // Progresso em relação à meta
    const progressPercent = dailyGoal > 0 ? (todayProfit / dailyGoal) * 100 : 0
    const goalReached = todayProfit >= dailyGoal

    return {
      dailyGoal,
      todayProfit,
      todayTotalTrades,
      todayWins,
      progressPercent,
      goalReached,
    }
  }, [trades, currentBalance, goalPercent])

  // Recomendação personalizada baseada no resultado do dia
  const recommendation = useMemo(() => {
    const { todayProfit, goalReached, todayTotalTrades } = dailyGoalData

    if (todayProfit > 0) {
      if (goalReached) {
        return {
          icon: CheckCircle2,
          iconColor: 'text-emerald-500',
          bgColor: 'bg-emerald-500/10',
          borderColor: 'border-emerald-500/20',
          title: 'Meta Batida!',
          message: 'Parabéns! Você atingiu sua meta diária. Proteja seus ganhos e considere parar de operar.',
        }
      }
      return {
        icon: TrendingUp,
        iconColor: 'text-emerald-500',
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/20',
        title: 'No Caminho Certo',
        message: 'Você está lucrando hoje! Continue disciplinado e siga sua estratégia.',
      }
    }

    if (todayProfit < 0) {
      return {
        icon: AlertTriangle,
        iconColor: 'text-rose-500',
        bgColor: 'bg-rose-500/10',
        borderColor: 'border-rose-500/20',
        title: 'Atenção!',
        message: 'Dia no vermelho. Evite operar para recuperar perdas — isso geralmente piora a situação. Descanse e volte amanhã.',
      }
    }

    // Empate (todayProfit === 0)
    if (todayTotalTrades > 0) {
      return {
        icon: Minus,
        iconColor: 'text-amber-500',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/20',
        title: 'Dia Neutro',
        message: 'Resultado zerado. Seja paciente e aguarde setups de alta probabilidade.',
      }
    }

    return {
      icon: Clock,
      iconColor: 'text-muted-foreground',
      bgColor: 'bg-muted/50',
      borderColor: 'border-border',
      title: 'Aguardando',
      message: 'Nenhuma operação hoje. Analise o mercado com calma antes de entrar.',
    }
  }, [dailyGoalData])

  const isLoading = tradesLoading || balanceLoading

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-sm text-muted-foreground">Carregando dados...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* SEÇÃO META DIÁRIA */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-foreground">Meta Diária</h2>
          <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full">
            Hoje
          </span>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {/* Card: Sua Meta */}
          <div className="group relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 transition-all hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5">
            <div className="absolute top-0 right-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl -mr-12 -mt-12 transition-all group-hover:bg-primary/20" />
            <div className="relative p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                    Sua Meta Hoje
                    <InfoTooltip content={GOAL_TOOLTIPS.suaMeta} />
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(dailyGoalData.dailyGoal)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {goalPercent}% do saldo atual
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Card: Lucro Hoje */}
          <div className={cn(
            "group relative overflow-hidden rounded-xl border transition-all hover:shadow-lg",
            dailyGoalData.todayProfit > 0 && "border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 hover:border-emerald-500/40 hover:shadow-emerald-500/5",
            dailyGoalData.todayProfit < 0 && "border-rose-500/20 bg-gradient-to-br from-rose-500/5 to-rose-500/10 hover:border-rose-500/40 hover:shadow-rose-500/5",
            dailyGoalData.todayProfit === 0 && "border-border/50 bg-gradient-to-br from-muted/30 to-muted/50 hover:border-border"
          )}>
            <div className={cn(
              "absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl -mr-12 -mt-12 transition-all",
              dailyGoalData.todayProfit > 0 && "bg-emerald-500/10 group-hover:bg-emerald-500/20",
              dailyGoalData.todayProfit < 0 && "bg-rose-500/10 group-hover:bg-rose-500/20",
              dailyGoalData.todayProfit === 0 && "bg-muted/50"
            )} />
            <div className="relative p-5">
              <div className="flex items-start gap-4">
                <div className={cn(
                  "flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center",
                  dailyGoalData.todayProfit > 0 && "bg-emerald-500/20",
                  dailyGoalData.todayProfit < 0 && "bg-rose-500/20",
                  dailyGoalData.todayProfit === 0 && "bg-muted"
                )}>
                  {dailyGoalData.todayProfit >= 0 ? (
                    <TrendingUp className={cn("h-6 w-6", dailyGoalData.todayProfit > 0 ? "text-emerald-500" : "text-muted-foreground")} />
                  ) : (
                    <TrendingDown className="h-6 w-6 text-rose-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                    Lucro Hoje
                    <InfoTooltip content={GOAL_TOOLTIPS.lucroHoje} />
                  </p>
                  <p className={cn(
                    "text-2xl font-bold",
                    dailyGoalData.todayProfit > 0 && "text-emerald-500",
                    dailyGoalData.todayProfit < 0 && "text-rose-500",
                    dailyGoalData.todayProfit === 0 && "text-muted-foreground"
                  )}>
                    {dailyGoalData.todayProfit >= 0 ? '' : '-'}
                    {formatCurrency(Math.abs(dailyGoalData.todayProfit))}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {dailyGoalData.todayTotalTrades} operações ({dailyGoalData.todayWins} wins)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Card: Progresso */}
          <div className={cn(
            "group relative overflow-hidden rounded-xl border transition-all hover:shadow-lg",
            dailyGoalData.goalReached
              ? "border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 hover:border-emerald-500/40 hover:shadow-emerald-500/5"
              : "border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-amber-500/10 hover:border-amber-500/40 hover:shadow-amber-500/5"
          )}>
            <div className={cn(
              "absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl -mr-12 -mt-12 transition-all",
              dailyGoalData.goalReached
                ? "bg-emerald-500/10 group-hover:bg-emerald-500/20"
                : "bg-amber-500/10 group-hover:bg-amber-500/20"
            )} />
            <div className="relative p-5">
              <div className="flex items-start gap-4">
                <div className={cn(
                  "flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center",
                  dailyGoalData.goalReached ? "bg-emerald-500/20" : "bg-amber-500/20"
                )}>
                  {dailyGoalData.goalReached ? (
                    <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                  ) : (
                    <Target className="h-6 w-6 text-amber-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                    Progresso
                    <InfoTooltip content={GOAL_TOOLTIPS.progresso} />
                  </p>
                  {dailyGoalData.goalReached ? (
                    <>
                      <p className="text-xl font-bold text-emerald-500">META BATIDA!</p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 font-medium">
                        Proteja seu lucro
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <p className="text-2xl font-bold text-foreground">
                          {Math.min(dailyGoalData.progressPercent, 100).toFixed(0)}%
                        </p>
                        <span className="text-xs text-muted-foreground">da meta</span>
                      </div>
                      <div className="w-full h-2 bg-muted rounded-full mt-2 overflow-hidden">
                        <div
                          className={cn(
                            "h-full rounded-full transition-all duration-500",
                            dailyGoalData.todayProfit >= 0 ? "bg-amber-500" : "bg-rose-500"
                          )}
                          style={{ width: `${Math.max(0, Math.min(dailyGoalData.progressPercent, 100))}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Faltam <strong className="text-amber-500">{formatCurrency(Math.max(0, dailyGoalData.dailyGoal - dailyGoalData.todayProfit))}</strong>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card: Como Calcular */}
        <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 transition-all hover:border-border hover:shadow-lg">
          <div className="p-5">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-bold text-foreground">Como calcular sua meta diária?</h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    Dica
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  Recomendamos que sua banca seja de <strong className="text-foreground">20x a 100x</strong> a meta diária de lucro.
                  Ou seja, sua meta diária deve ser entre <strong className="text-foreground">1% e 5%</strong> do seu capital.
                </p>
                {/* Recomendação Personalizada */}
                <div className={cn(
                  "mt-4 p-3 rounded-lg border",
                  recommendation.bgColor,
                  recommendation.borderColor
                )}>
                  <div className="flex items-start gap-3">
                    <recommendation.icon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", recommendation.iconColor)} />
                    <div>
                      <p className={cn("text-sm font-semibold", recommendation.iconColor)}>
                        {recommendation.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {recommendation.message}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEÇÃO: PLANILHA SIGMA */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-foreground">Planilha SIGMA</h2>
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {totals.totalEntradas} operações
            </span>
          </div>
          <SpreadsheetControls
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
            riskProfile={riskProfile}
            onRiskProfileChange={setRiskProfile}
            currentBalance={currentBalance}
            goalPercent={goalPercent}
            onGoalPercentChange={setGoalPercent}
          />
        </div>

        {/* Tabela */}
        <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-br from-muted/10 to-muted/20">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50 bg-muted/40">
                  <th className="py-3.5 px-4 text-left font-semibold text-foreground whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      DIA
                    </div>
                  </th>
                  <th className="py-3.5 px-4 text-right font-semibold text-foreground whitespace-nowrap">CAPITAL</th>
                  <th className="py-3.5 px-4 text-center font-semibold text-foreground whitespace-nowrap">GESTÃO</th>
                  <th className="py-3.5 px-4 text-right font-semibold text-primary whitespace-nowrap">Meta do dia</th>
                  <th className="py-3.5 px-4 text-right font-semibold text-foreground whitespace-nowrap">ENTRADA</th>
                  <th className="py-3.5 px-4 text-center font-semibold text-foreground whitespace-nowrap">QTD</th>
                  <th className="py-3.5 px-4 text-center font-semibold text-foreground whitespace-nowrap">Meta</th>
                  <th className="py-3.5 px-4 text-right font-semibold text-foreground whitespace-nowrap" colSpan={2}>
                    <div className="flex items-center justify-end gap-2">
                      RESULTADO
                      <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {rows.map((row, index) => {
                  // Parse date correctly to avoid timezone issues
                  // new Date("2024-12-09") interprets as UTC, causing wrong day in local timezone
                  const [year, month, day] = row.date.split('-').map(Number)
                  const isCurrentDay = isToday(new Date(year, month - 1, day))
                  const hasOperations = row.qtEntradas > 0

                  return (
                    <tr
                      key={row.day}
                      className={cn(
                        'transition-all duration-150',
                        index % 2 === 0 ? 'bg-transparent' : 'bg-muted/10',
                        hasOperations && 'hover:bg-muted/30',
                        !hasOperations && 'opacity-40',
                        isCurrentDay && 'bg-primary/5 hover:bg-primary/10 ring-1 ring-inset ring-primary/20'
                      )}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "font-semibold tabular-nums",
                            isCurrentDay && "text-primary"
                          )}>
                            {String(row.day).padStart(2, '0')}
                          </span>
                          {isCurrentDay && (
                            <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                              Hoje
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono text-xs text-muted-foreground">
                          {formatCurrency(row.capitalAtual)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-xs font-medium text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                          {row.valorOrdem}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono text-xs font-semibold text-primary">
                          {formatCurrency(row.metaDoDia)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono text-xs text-muted-foreground">
                          {row.valorPorEntrada > 0 ? formatCurrency(row.valorPorEntrada) : (
                            <Minus className="h-3 w-3 text-muted-foreground/50 inline" />
                          )}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {row.qtEntradas > 0 ? (
                          <span className="inline-flex items-center justify-center min-w-[24px] h-6 text-xs font-semibold bg-muted/50 rounded-full px-2">
                            {row.qtEntradas}
                          </span>
                        ) : (
                          <Minus className="h-3 w-3 text-muted-foreground/50 mx-auto" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {row.metaBatida ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto" />
                        ) : row.qtEntradas > 0 ? (
                          <Minus className="h-3 w-3 text-muted-foreground/50 mx-auto" />
                        ) : null}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {row.lucroDiaReais !== 0 ? (
                          <div className="flex items-center justify-end gap-1.5">
                            {row.lucroDiaReais > 0 ? (
                              <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                            ) : (
                              <TrendingDown className="h-3.5 w-3.5 text-rose-500" />
                            )}
                            <span className={cn(
                              "font-mono text-xs font-semibold",
                              row.lucroDiaReais > 0 && "text-emerald-500",
                              row.lucroDiaReais < 0 && "text-rose-500"
                            )}>
                              {row.lucroDiaReais > 0 ? '+' : ''}{formatCurrency(row.lucroDiaReais)}
                            </span>
                          </div>
                        ) : (
                          <Minus className="h-3 w-3 text-muted-foreground/50 ml-auto" />
                        )}
                      </td>
                      <td className={cn(
                        "py-3 px-4 text-right w-20",
                        row.lucroDiaReais > 0 && "bg-emerald-500/5",
                        row.lucroDiaReais < 0 && "bg-rose-500/5"
                      )}>
                        {row.lucroDiaReais !== 0 ? (
                          <span className={cn(
                            "font-mono text-xs font-medium",
                            row.lucroDiaReais > 0 && "text-emerald-500",
                            row.lucroDiaReais < 0 && "text-rose-500"
                          )}>
                            {formatPercent(row.lucroDiaPercent)}
                          </span>
                        ) : (
                          <Minus className="h-3 w-3 text-muted-foreground/50 ml-auto" />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Resumo do Mês - Cards */}
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          {/* Total Operações */}
          <div className="rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Operações</span>
            </div>
            <p className="text-xl font-bold text-foreground">{totals.totalEntradas}</p>
          </div>

          {/* Dias Operados */}
          <div className="rounded-xl border border-border/50 bg-gradient-to-br from-muted/20 to-muted/40 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Dias Operados</span>
            </div>
            <p className="text-xl font-bold text-foreground">{rows.filter(r => r.qtEntradas > 0).length}</p>
          </div>

          {/* Lucro Total */}
          <div className={cn(
            "rounded-xl border p-4",
            totals.totalLucroReais > 0 && "border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-emerald-500/10",
            totals.totalLucroReais < 0 && "border-rose-500/20 bg-gradient-to-br from-rose-500/5 to-rose-500/10",
            totals.totalLucroReais === 0 && "border-border/50 bg-gradient-to-br from-muted/20 to-muted/40"
          )}>
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className={cn(
                "h-4 w-4",
                totals.totalLucroReais > 0 && "text-emerald-500",
                totals.totalLucroReais < 0 && "text-rose-500",
                totals.totalLucroReais === 0 && "text-muted-foreground"
              )} />
              <span className="text-xs font-medium text-muted-foreground">Lucro do Mês</span>
            </div>
            <p className={cn(
              "text-xl font-bold",
              totals.totalLucroReais > 0 && "text-emerald-500",
              totals.totalLucroReais < 0 && "text-rose-500",
              totals.totalLucroReais === 0 && "text-foreground"
            )}>
              {totals.totalLucroReais >= 0 ? '' : '-'}{formatCurrency(Math.abs(totals.totalLucroReais))}
            </p>
          </div>

          {/* Rendimento % */}
          <div className={cn(
            "rounded-xl border p-4",
            totals.totalLucroReais > 0 && "border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-emerald-500/10",
            totals.totalLucroReais < 0 && "border-rose-500/20 bg-gradient-to-br from-rose-500/5 to-rose-500/10",
            totals.totalLucroReais === 0 && "border-border/50 bg-gradient-to-br from-muted/20 to-muted/40"
          )}>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className={cn(
                "h-4 w-4",
                totals.totalLucroReais > 0 && "text-emerald-500",
                totals.totalLucroReais < 0 && "text-rose-500",
                totals.totalLucroReais === 0 && "text-muted-foreground"
              )} />
              <span className="text-xs font-medium text-muted-foreground">Rendimento</span>
            </div>
            <p className={cn(
              "text-xl font-bold",
              totals.totalLucroReais > 0 && "text-emerald-500",
              totals.totalLucroReais < 0 && "text-rose-500",
              totals.totalLucroReais === 0 && "text-foreground"
            )}>
              {formatPercent(totals.totalLucroPercent)}
            </p>
          </div>
        </div>
      </div>

      {/* SEÇÃO: REGRAS DE GESTÃO */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-white" />
          <h2 className="text-lg font-semibold text-foreground">Regras de Gestão</h2>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Card: Bateu a Meta */}
          <div className="group relative overflow-hidden rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 transition-all hover:border-emerald-500/40 hover:shadow-lg hover:shadow-emerald-500/5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-emerald-500/20" />
            <div className="relative p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                      Regra #1
                    </span>
                  </div>
                  <h3 className="font-bold text-foreground mb-1">Bateu a Meta, Sai do Mercado!</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Alcançou sua meta diária de <strong className="text-emerald-500">1% a 5%</strong>? Pare de operar e proteja seus ganhos.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Card: Regra de Loss */}
          <div className="group relative overflow-hidden rounded-xl border border-rose-500/20 bg-gradient-to-br from-rose-500/5 to-rose-500/10 transition-all hover:border-rose-500/40 hover:shadow-lg hover:shadow-rose-500/5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/10 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:bg-rose-500/20" />
            <div className="relative p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-rose-500/20 flex items-center justify-center">
                  <XCircle className="h-6 w-6 text-rose-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-rose-500 bg-rose-500/10 px-2 py-0.5 rounded-full">
                      Regra #2
                    </span>
                  </div>
                  <h3 className="font-bold text-foreground mb-1">2 Loss Seguidos = Stop</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Após 2 perdas consecutivas, <strong className="text-rose-500">saia do mercado</strong> ou troque de ativo. Retorne na próxima sessão.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEÇÃO: CAPITAL E RECOMENDAÇÕES */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-white" />
          <h2 className="text-lg font-semibold text-foreground">Capital e Recomendações</h2>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Card: Classificação de Capital */}
          <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-br from-muted/30 to-muted/50 transition-all hover:border-border hover:shadow-lg">
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">Classificação de Capital</h3>
                  <p className="text-xs text-muted-foreground">Onde você está?</p>
                </div>
              </div>

              <div className="space-y-2">
                {/* Capital Ruim */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-rose-500/5 border border-rose-500/10">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-rose-500" />
                    <span className="text-sm font-medium text-foreground">Iniciante</span>
                  </div>
                  <span className="text-sm font-mono text-rose-500">R$ 100 - 5.000</span>
                </div>

                {/* Capital Mediano */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-foreground">Intermediário</span>
                  </div>
                  <span className="text-sm font-mono text-amber-500">R$ 5.001 - 9.999</span>
                </div>

                {/* Capital Bom */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm font-medium text-foreground">Profissional</span>
                  </div>
                  <span className="text-sm font-mono text-emerald-500">R$ 10.000+</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card: Capital Recomendado */}
          <div className="group relative overflow-hidden rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-amber-500/10 transition-all hover:border-amber-500/40 hover:shadow-lg hover:shadow-amber-500/5">
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl -ml-20 -mb-20 transition-all group-hover:bg-amber-500/20" />
            <div className="relative p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full">
                    Recomendação
                  </span>
                </div>
              </div>

              <div className="text-center py-4">
                <p className="text-3xl font-bold text-foreground mb-1">
                  R$ 10.000 <span className="text-muted-foreground font-normal">a</span> R$ 100.000
                </p>
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                  Capital Ideal para Operar
                </p>
              </div>

              <div className="mt-4 p-3 rounded-lg bg-background/50 border border-border/50">
                <p className="text-xs text-muted-foreground text-center leading-relaxed">
                  <strong className="text-foreground">Maior capital = Maior lucro + Menor risco.</strong>
                  <br />
                  Operações ficam mais fáceis e lucrativas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
